import { useEffect, useMemo, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useAuth0 } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faEdit, faLayerGroup, faTable, faUsers } from '@fortawesome/free-solid-svg-icons';
import EditTableModal from '../modals/EditTableModal';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminEmptyState from '../shared/AdminEmptyState';
import AdminFilterBar from '../shared/AdminFilterBar';
import AdminSectionCard from '../shared/AdminSectionCard';
import '../../styles/route_pages/Tables.css';
import { reorderTables } from '../../utils/tableService';
import { fetchOrders } from '../../utils/orderService';
import { useVenueId } from '../../hooks/useUserData';
import { useAuth } from '../../AuthenticatedApp';
import LoadingSpinner from '../LoadingSpinner';
import { resolveAdminTableDisplayStatus, TABLE_STATUS_LABELS } from '../../utils/tableDisplayState';

const OCCUPIED_ORDER_STATUSES = new Set(['pending', 'preparing', 'completed']);

const Tables = () => {
  const venueId = useVenueId();
  const { floors, tables, setTables } = useAdmin();
  const { getAccessTokenSilently } = useAuth0();
  const { hasPermission } = useAuth();
  const [selectedFloor, setSelectedFloor] = useState(floors[0]?.id);
  const [filteredTables, setFilteredTables] = useState([]);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentTable, setCurrentTable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [occupiedTableIds, setOccupiedTableIds] = useState(new Set());

  useEffect(() => {
    if (floors.length > 0 && !selectedFloor) {
      setSelectedFloor(floors[0].id);
    }
  }, [floors, selectedFloor]);

  useEffect(() => {
    if (selectedFloor) {
      setFilteredTables(
        tables
          .filter((table) => table.floorId === selectedFloor)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
      );
    }
  }, [selectedFloor, tables]);

  useEffect(() => {
    let isActive = true;

    const canReadOrders = hasPermission('view_orders') || hasPermission('edit_orders');
    if (!venueId || !canReadOrders) {
      setOccupiedTableIds(new Set());
      return () => {
        isActive = false;
      };
    }

    const loadOrders = async () => {
      try {
        const orders = await fetchOrders(venueId, getAccessTokenSilently);
        if (!isActive) {
          return;
        }

        const nextOccupiedTableIds = new Set(
          orders
            .filter((order) => order?.paymentStatus === 'unpaid' && OCCUPIED_ORDER_STATUSES.has(order?.status))
            .map((order) => order.tableId),
        );

        setOccupiedTableIds(nextOccupiedTableIds);
      } catch (error) {
        if (!isActive) {
          return;
        }

        setOccupiedTableIds(new Set());
      }
    };

    loadOrders();

    return () => {
      isActive = false;
    };
  }, [getAccessTokenSilently, hasPermission, venueId]);

  const floorOptions = useMemo(
    () =>
      floors.map((floor) => ({
        value: floor.id,
        label: floor.name,
        count: tables.filter((table) => table.floorId === floor.id).length,
      })),
    [floors, tables],
  );

  const handleMoveTable = async (tableToMove, direction) => {
    if (loading) {
      return;
    }

    const currentIndex = filteredTables.findIndex((table) => table.id === tableToMove.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= filteredTables.length) {
      return;
    }

    try {
      setLoading(true);
      const reorderedTables = Array.from(filteredTables);
      [reorderedTables[currentIndex], reorderedTables[newIndex]] = [reorderedTables[newIndex], reorderedTables[currentIndex]];
      const tablesWithNewOrder = reorderedTables.map((table, index) => ({ id: table.id, sortOrder: index }));
      await reorderTables(venueId, tablesWithNewOrder, getAccessTokenSilently);

      const updatedTables = tables.map((table) => {
        const newOrder = tablesWithNewOrder.find((entry) => entry.id === table.id);
        return newOrder ? { ...table, sortOrder: newOrder.sortOrder } : table;
      });

      setTables(updatedTables);
    } catch (error) {
      console.error('Failed to move table:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!floors.length) {
    return (
      <div className="page-container">
        <AdminPageHeader title="Masa Yönetimi" subtitle="Kat bazında masa sırasını ve temel masa bilgilerini düzenleyin." />
        <AdminEmptyState
          icon={faLayerGroup}
          title="Henüz kat bulunamadı"
          description="Masa oluşturmak için katlar sekmesinden en az bir kat oluşturmanız gerekiyor."
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      <AdminPageHeader
        title="Masa Yönetimi"
        subtitle="Seçili kata ait masa sırasını, kapasite bilgisini ve QR operasyonunu daha okunur bir yerleşim kartı düzeninde yönetin."
      />

      <div className="admin-page-stack">
        <AdminSectionCard>
          <div className="tables-surface-toolbar">
            <AdminFilterBar
              options={floorOptions}
              value={selectedFloor}
              onChange={setSelectedFloor}
              ariaLabel="Kat seçimi"
              className="tables-minimal-filter"
            />
          </div>
          {loading ? <LoadingSpinner /> : null}
          {filteredTables.length === 0 ? (
            <AdminEmptyState
              icon={faTable}
              title="Bu katta henüz masa bulunmuyor"
              description="Seçili kata masa eklediğinizde burada daha düzenli bir yerleşim listesi görünecek."
            />
          ) : (
            <div className={`tables-management-grid ${loading ? 'loading' : ''}`}>
              {filteredTables.map((table, index) => {
                const displayStatus = resolveAdminTableDisplayStatus(table, occupiedTableIds);

                return (
                <article key={table.id} className="tables-management-card">
                  <div className="tables-management-card-header">
                    <div>
                      <h3 className="tables-management-card-title">{table.name}</h3>
                      <p className="tables-management-card-subtitle">
                        {floors.find((floor) => floor.id === table.floorId)?.name || 'Kat bilinmiyor'}
                      </p>
                    </div>
                    <span className={`admin-chip tables-status-chip tables-status-${displayStatus}`}>
                      {TABLE_STATUS_LABELS[displayStatus]}
                    </span>
                  </div>

                  <div className="tables-management-card-body">
                    <div className="tables-management-stat">
                      <FontAwesomeIcon icon={faUsers} />
                      <span>{table.capacity || '?'} kişilik</span>
                    </div>
                    <div className="tables-management-stat">
                      <FontAwesomeIcon icon={faTable} />
                      <span>QR {table.qrCode ? 'hazır' : 'bekliyor'}</span>
                    </div>
                  </div>

                  <div className="tables-management-card-footer">
                    <div className="admin-action-cluster tables-action-cluster">
                      <button
                        type="button"
                        className="admin-icon-button tables-action-icon"
                        onClick={() => handleMoveTable(table, 'up')}
                        disabled={loading || index === 0}
                        title="Masa sırasını sola taşı"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <button
                        type="button"
                        className="admin-icon-button tables-action-icon"
                        onClick={() => handleMoveTable(table, 'down')}
                        disabled={loading || index === filteredTables.length - 1}
                        title="Masa sırasını sağa taşı"
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm tables-edit-button"
                        onClick={() => { setCurrentTable(table); setIsEditModalVisible(true); }}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                        Düzenle
                      </button>
                    </div>
                  </div>
                </article>
              )})}
            </div>
          )}
        </AdminSectionCard>
      </div>

      {currentTable ? (
        <EditTableModal show={isEditModalVisible} onClose={() => setIsEditModalVisible(false)} table={currentTable} />
      ) : null}
    </div>
  );
};

export default Tables;
